// Each collection lives at /collections/:slug. The slug maps to a category
// name in `product_categories.name`; the rest is editorial content for the
// storytelling structure of each page.

export type CollectionStoryChapter = {
  eyebrow: string;
  heading: string;
  body: string;
  image: string;
  imageAlt: string;
};

export type CollectionContent = {
  slug: string;
  // Must match the `name` column in product_categories.
  categoryName: string;
  // Hero
  eyebrow: string;
  headline: string;
  intro: string;
  heroImage: string;
  heroAlt: string;
  // A short list of qualities under the hero (icon chips)
  attributes: Array<{ icon: string; label: string }>;
  // One big story moment further down the page
  chapter: CollectionStoryChapter;
  // Closing line that sits above the product grid
  productsLabel: string;
};

export const collections: CollectionContent[] = [
  {
    slug: "bedroom",
    categoryName: "Bedroom",
    eyebrow: "The Bedroom collection",
    headline: "Made to be\nslept in.",
    intro:
      "Soft-washed linen, hand-finished blankets, and quiet textiles for the room that should feel the quietest.",
    heroImage:
      "https://readdy.ai/api/search-image?query=An%20overhead%20flat%20lay%20top%20down%20view%20of%20a%20rumpled%20oatmeal%20linen%20bedsheet%20with%20soft%20natural%20folds%20a%20sage%20green%20wool%20knit%20throw%20blanket%20folded%20at%20one%20corner%20two%20pintuck%20pillow%20shams%20stacked%20a%20sprig%20of%20dried%20eucalyptus%20and%20a%20small%20open%20hardcover%20book%20resting%20on%20top%20warm%20natural%20late%20morning%20light%20filtering%20across%20the%20fabric%20muted%20cream%20and%20sage%20palette%20editorial%20documentary%20photography%20soft%20diffused%20shadows%20wide%20horizontal%20minimalist%20textile%20still%20life%20mood&width=2200&height=1000&seq=collection-bedroom-hero-v4-overhead-linen&orientation=landscape",
    heroAlt: "Linen and wool textiles for the bedroom",
    attributes: [
      { icon: "ri-recycle-line", label: "Stonewashed linen" },
      { icon: "ri-leaf-line", label: "Natural fibers" },
      { icon: "ri-hand-heart-line", label: "Hand-finished seams" },
    ],
    chapter: {
      eyebrow: "Why it's softer on day one",
      heading: "Pre-loved by design.",
      body: "Every linen piece is stonewashed before it leaves the studio — softer the day you unpack it than the day it was made. Pillow shams are hand-finished, never machine-overlocked, so you'll feel the difference the first night.",
      image:
        "https://readdy.ai/api/search-image?query=A%20close%20up%20detail%20of%20folded%20stonewashed%20oatmeal%20linen%20sheets%20with%20a%20hand%20stitched%20seam%20resting%20on%20a%20worn%20oak%20farmhouse%20dresser%20a%20single%20sprig%20of%20dried%20lavender%20on%20top%20warm%20natural%20morning%20light%20from%20a%20side%20window%20muted%20cream%20and%20sage%20color%20palette%20editorial%20documentary%20photography%20soft%20shadows%20simple%20clean%20uncluttered%20background%20cozy%20textile%20detail%20mood&width=1400&height=1700&seq=collection-bedroom-story-v2&orientation=portrait",
      imageAlt: "Stonewashed linen folded on a farmhouse dresser",
    },
    productsLabel: "Pieces in this collection",
  },
  {
    slug: "wall-art",
    categoryName: "Wall Art",
    eyebrow: "The Wall Art collection",
    headline: "Words you'll\nkeep on the wall.",
    intro:
      "Hand-lettered signs, framed prints, and small works on paper — each one finished in the studio, never printed in a factory.",
    heroImage:
      "https://readdy.ai/api/search-image?query=A%20wide%20warm%20cream%20plaster%20studio%20wall%20hung%20with%20six%20hand%20lettered%20wooden%20signs%20and%20small%20framed%20botanical%20prints%20arranged%20in%20a%20loose%20editorial%20salon%20style%20gallery%20wall%20a%20long%20worn%20oak%20ledge%20running%20horizontally%20below%20holding%20a%20single%20tall%20stoneware%20bud%20vase%20with%20a%20wheat%20stem%20warm%20low%20afternoon%20sun%20raking%20across%20the%20wall%20from%20the%20left%20muted%20terracotta%20cream%20and%20honey%20palette%20editorial%20documentary%20photography%20soft%20long%20shadows%20wide%20horizontal%20gallery%20composition%20heirloom%20craft%20mood&width=2200&height=1000&seq=collection-wallart-hero-v4-gallery-wall&orientation=landscape",
    heroAlt: "A gallery wall of hand-lettered signs and prints",
    attributes: [
      { icon: "ri-quill-pen-line", label: "Hand-lettered" },
      { icon: "ri-drop-line", label: "Archival inks" },
      { icon: "ri-shield-star-line", label: "Sealed with beeswax" },
    ],
    chapter: {
      eyebrow: "How it's made",
      heading: "Lettered by hand.\nEvery letter.",
      body: "Each sign is drawn freehand in pencil, traced in archival ink, and sealed with beeswax. No two are exact. The wood comes from reclaimed barn boards sourced within a hundred miles of the studio.",
      image:
        "https://readdy.ai/api/search-image?query=A%20close%20up%20detail%20of%20a%20hand%20lettered%20wooden%20sign%20in%20progress%20with%20pencil%20and%20black%20ink%20brush%20on%20a%20worn%20oak%20studio%20workbench%20a%20small%20jar%20of%20black%20ink%20and%20a%20linen%20cloth%20nearby%20warm%20natural%20afternoon%20light%20from%20a%20studio%20window%20muted%20cream%20and%20warm%20wood%20color%20palette%20editorial%20documentary%20photography%20soft%20shadows%20simple%20clean%20uncluttered%20background%20craft%20process%20mood&width=1400&height=1700&seq=collection-wallart-story-v2&orientation=portrait",
      imageAlt: "Hand-lettering a sign in the studio",
    },
    productsLabel: "Works in this collection",
  },
  {
    slug: "kitchenware",
    categoryName: "Kitchenware",
    eyebrow: "The Kitchenware collection",
    headline: "Built for\nslow mornings.",
    intro:
      "Stoneware mugs, hand-turned plates, and linen tea towels for kitchens that get used every day.",
    heroImage:
      "https://readdy.ai/api/search-image?query=Two%20long%20open%20wooden%20farmhouse%20pantry%20shelves%20running%20horizontally%20across%20a%20wall%20stacked%20with%20rows%20of%20hand%20turned%20honey%20glazed%20stoneware%20mugs%20hanging%20from%20small%20hooks%20plates%20leaning%20upright%20a%20stoneware%20pitcher%20a%20couple%20of%20folded%20linen%20tea%20towels%20in%20a%20small%20woven%20basket%20a%20warm%20cream%20plaster%20wall%20behind%20soft%20afternoon%20side%20light%20raking%20across%20the%20shelves%20muted%20honey%20cream%20and%20warm%20earth%20palette%20editorial%20documentary%20photography%20soft%20shadows%20symmetrical%20wide%20horizontal%20pantry%20composition%20cozy%20kitchen%20mood&width=2200&height=1000&seq=collection-kitchen-hero-v4-pantry-shelf&orientation=landscape",
    heroAlt: "A pantry shelf of stoneware mugs and plates",
    attributes: [
      { icon: "ri-fire-line", label: "Wood-fired stoneware" },
      { icon: "ri-leaf-line", label: "Iowa river clay" },
      { icon: "ri-cup-line", label: "Made to be used" },
    ],
    chapter: {
      eyebrow: "From clay to kiln",
      heading: "Wheel-thrown.\nWood-fired.",
      body: "Iowa river clay, hand-turned on a single wheel, and fired in a wood kiln behind the studio — every piece keeps the ash from the wood that lit it. That's why no two glazes are identical.",
      image:
        "https://readdy.ai/api/search-image?query=A%20close%20up%20detail%20of%20a%20potters%20hands%20shaping%20a%20stoneware%20mug%20on%20a%20pottery%20wheel%20wet%20honey%20brown%20clay%20a%20small%20wooden%20tool%20resting%20nearby%20warm%20natural%20afternoon%20light%20from%20a%20studio%20window%20muted%20cream%20and%20warm%20earth%20color%20palette%20editorial%20documentary%20photography%20soft%20shadows%20simple%20clean%20uncluttered%20studio%20background%20craft%20process%20mood&width=1400&height=1700&seq=collection-kitchen-story-v2&orientation=portrait",
      imageAlt: "Throwing stoneware on the wheel",
    },
    productsLabel: "Pieces for the kitchen",
  },
];

export function getCollectionBySlug(slug: string): CollectionContent | undefined {
  return collections.find((c) => c.slug === slug);
}

// Returns the next collection in the cycle (wraps back to the first).
export function getNextCollection(slug: string): CollectionContent | undefined {
  const i = collections.findIndex((c) => c.slug === slug);
  if (i === -1) return undefined;
  return collections[(i + 1) % collections.length];
}